import { createApiClient } from '@room/frontend-sdk';
import type { APIResponse } from '@room/shared-ui';

const apiClient = createApiClient({
  baseURL: '/api',
});

const logApiError = (method: string, url: string, error: unknown) => {
  const errorInfo = {
    timestamp: new Date().toISOString(),
    method,
    url,
    error: (error as Error)?.message || String(error),
  };
  console.error('[API Error]', errorInfo);
};

export const api = {
  async get<T = unknown>(url: string): Promise<T> {
    try {
      return await apiClient.get<T>(url);
    } catch (error) {
      logApiError('GET', url, error);
      throw error;
    }
  },
  async post<T = unknown>(url: string, data?: unknown): Promise<T> {
    try {
      return await apiClient.post<T>(url, data);
    } catch (error) {
      logApiError('POST', url, error);
      throw error;
    }
  },
  async put<T = unknown>(url: string, data?: unknown): Promise<T> {
    try {
      return await apiClient.put<T>(url, data);
    } catch (error) {
      logApiError('PUT', url, error);
      throw error;
    }
  },
  async patch<T = unknown>(url: string, data?: unknown): Promise<T> {
    try {
      return await apiClient.patch<T>(url, data);
    } catch (error) {
      logApiError('PATCH', url, error);
      throw error;
    }
  },
  async delete<T = unknown>(url: string): Promise<T> {
    try {
      return await apiClient.delete<T>(url);
    } catch (error) {
      logApiError('DELETE', url, error);
      throw error;
    }
  },
};

export const pluginAPI = {
  getMyPlugins: () => api.get<APIResponse<{ plugins: any[] }>>('/developer/plugins/my-plugins'),
  getUserRegistry: () => api.get<APIResponse<{ plugins: Array<{ pluginId: string; state: string }> }>>('/plugins/registry'),
};

export const pipelineAPI = {
  getSnapshot: () => api.get<APIResponse<any>>('/developer/pipeline'),
  getDetail: (id: string) => api.get<APIResponse<any>>(`/developer/pipeline/${id}`),
  approve: (id: string, payload?: { note?: string }) =>
    api.post<APIResponse>(`/developer/pipeline/${id}/approve`, payload),
  reject: (id: string, payload?: { note?: string }) =>
    api.post<APIResponse>(`/developer/pipeline/${id}/reject`, payload),
  requestChanges: (id: string, payload?: { note?: string }) =>
    api.post<APIResponse>(`/developer/pipeline/${id}/request-changes`, payload),
};

export const marketplaceAPI = {
  listPlugins: () => api.get<APIResponse<{ plugins: any[] }>>('/marketplace/plugins'),
  installPlugin: (id: string) => api.post<APIResponse>(`/marketplace/plugins/${id}/install`),
  unpublish: (id: string) => api.delete<APIResponse>(`/marketplace/plugins/${id}`),
};

export const adminAPI = {
  getSystemSettings: () =>
    api.get<
      APIResponse<{
        guestLoginEnabled: boolean;
        paymentsEnabled: boolean;
        groupCreationEnabled: boolean;
        fileUploadEnabled: boolean;
        maxFileSizeMB: number;
        maxGroupMembers: number;
        updatedAt?: number;
      }>
    >('/admin/system-settings'),
  saveSystemSettings: (payload: {
    guestLoginEnabled: boolean;
    paymentsEnabled: boolean;
    groupCreationEnabled: boolean;
    fileUploadEnabled: boolean;
    maxFileSizeMB: number;
    maxGroupMembers: number;
  }) => api.post<APIResponse>('/admin/system-settings', payload),
  getSystemUsers: () => api.get<APIResponse<{ users: any[] }>>('/admin/system-users'),
  promoteSystemUser: (userId: string) =>
    api.post<APIResponse>('/admin/system-users/promote', { userId }),
  demoteSystemUser: (userId: string) =>
    api.post<APIResponse>('/admin/system-users/demote', { userId }),
  getDevConsoleSettings: () =>
    api.get<APIResponse<{ globalEnabled: boolean; overrides: Record<string, boolean> }>>(
      '/admin/dev-console-settings'
    ),
  saveDevConsoleSettings: (payload: {
    globalEnabled: boolean;
    overrides: Record<string, boolean>;
  }) => api.post<APIResponse>('/admin/dev-console-settings', payload),
  getConfigurations: (params?: { category?: string; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.category) query.set('category', params.category);
    if (params?.search) query.set('search', params.search);
    const suffix = query.toString();
    return api.get<APIResponse<any[]>>(`/admin/configurations${suffix ? `?${suffix}` : ''}`);
  },
  getConfiguration: (id: string) => api.get<APIResponse>(`/admin/configurations/${id}`),
  getPublicConfigs: () => api.get<APIResponse<Record<string, unknown>>>('/config'),
  getConfigurationHistory: (id: string) =>
    api.get<APIResponse>(`/admin/configurations/${id}/history`),
  getConfigurationCategories: () =>
    api.get<APIResponse<string[]>>('/admin/configurations/categories'),
  createConfiguration: (payload: {
    key: string;
    value: unknown;
    type: string;
    category: string;
    description?: string;
    isPublic?: boolean;
    isEditable?: boolean;
    validationSchema?: string | null;
  }) => api.post<APIResponse>('/admin/configurations', payload),
  updateConfiguration: (
    id: string,
    payload: {
      value?: unknown;
      description?: string | null;
      isPublic?: boolean;
      isEditable?: boolean;
      validationSchema?: string | null;
    }
  ) => api.put<APIResponse>(`/admin/configurations/${id}`, payload),
  deleteConfiguration: (id: string) => api.delete<APIResponse>(`/admin/configurations/${id}`),
};
